import { create } from "zustand";
import {
  applyTheme,
  readThemePreference,
  resolveTheme,
  systemPrefersLight,
  writeThemePreference,
} from "@/lib/theme";
import type { ResolvedTheme, ThemePreference } from "@/lib/theme";

type ToastType = "success" | "error" | "info";

/** Pourquoi une notification s'est fermée. */
export type ToastCloseReason = "action" | "timeout" | "dismiss";

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  /**
   * Un geste proposé dans la notification — « Annuler » surtout. Le cliquer
   * ferme la notification avec la raison `action`.
   */
  action?: { label: string };
  /** Durée d'affichage en ms, hors survol. Ignorée pour une erreur. */
  duration?: number;
  /**
   * Appelé une fois, quelle que soit la façon dont la notification se ferme.
   * C'est ce qui permet de différer un geste jusqu'à ce que « Annuler » ne
   * soit plus possible : la raison dit s'il faut l'exécuter.
   */
  onClose?: (reason: ToastCloseReason) => void;
}

/** Une notification telle qu'on la soumet : l'identifiant est posé par le store. */
export type ToastPayload = Omit<Toast, "id">;

interface UiState {
  sidebarOpen: boolean;
  sidebarMobileOpen: boolean;
  toasts: Toast[];
  /**
   * Le choix de l'utilisateur, pas le thème appliqué. « Système » n'est pas
   * une couleur : c'est une délégation, et la distinguer permet de suivre le
   * réglage du système quand il change en cours de session.
   */
  theme: ThemePreference;
  /**
   * Le thème réellement affiché — « système » résolu.
   *
   * Distinct du choix, parce que la couleur d'accent en dépend : l'encre de
   * marque doit rester lisible sur le fond de page, et ce fond change avec le
   * thème. Un composant qui ne connaîtrait que la préférence ne saurait pas
   * recalculer quand le système bascule.
   */
  resolvedTheme: ResolvedTheme;
  /**
   * La palette de création — un seul état pour le clavier, le bouton du menu
   * et le bouton flottant du téléphone : trois portes, une seule pièce.
   */
  paletteOpen: boolean;
  setPaletteOpen: (open: boolean) => void;
  /**
   * La photo d'un ticket prise depuis la palette, en attente de l'écran
   * Dépenses qui sait la lire.
   *
   * Un stockage plutôt qu'un paramètre d'URL : une photo ne tient pas dans une
   * adresse, et le sélecteur de fichier doit s'ouvrir sur le clic lui-même —
   * un navigateur refuse de l'ouvrir après une navigation.
   */
  pendingReceipt: File | null;
  setPendingReceipt: (file: File | null) => void;
  setSidebarOpen: (open: boolean) => void;
  setSidebarMobileOpen: (open: boolean) => void;
  setTheme: (preference: ThemePreference) => void;
  /** Appelée par l'écoute du réglage système. */
  syncResolvedTheme: (resolved: ResolvedTheme) => void;
  addToast: (t: Omit<Toast, "id">) => void;
  dismissToast: (id: string, reason?: ToastCloseReason) => void;
}

export const useUiStore = create<UiState>((set) => ({
  sidebarOpen: true,
  sidebarMobileOpen: false,
  paletteOpen: false,
  pendingReceipt: null,
  toasts: [],
  // Le script en ligne d'index.html a déjà posé le thème sur le document
  // avant le premier rendu ; on relit la même source pour que le store parte
  // d'accord avec ce qui est affiché.
  theme: readThemePreference(),
  resolvedTheme: resolveTheme(readThemePreference(), systemPrefersLight()),

  setPaletteOpen: (paletteOpen) => set({ paletteOpen }),
  setPendingReceipt: (pendingReceipt) => set({ pendingReceipt }),
  setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
  setSidebarMobileOpen: (sidebarMobileOpen) => set({ sidebarMobileOpen }),

  setTheme: (theme) => {
    const resolvedTheme = resolveTheme(theme, systemPrefersLight());
    writeThemePreference(theme);
    applyTheme(resolvedTheme);
    set({ theme, resolvedTheme });
  },

  syncResolvedTheme: (resolvedTheme) => {
    applyTheme(resolvedTheme);
    set({ resolvedTheme });
  },

  // Le minuteur ne vit plus ici mais dans ToastViewport : c'est lui qui sait
  // si le pointeur est posé sur la notification, et une erreur qu'on est en
  // train de lire ne doit pas s'effacer sous les yeux.
  addToast: (t) =>
    set((s) => ({ toasts: [...s.toasts, { ...t, id: crypto.randomUUID() }] })),

  dismissToast: (id, reason = "dismiss") => {
    const toast = useUiStore.getState().toasts.find((t) => t.id === id);
    if (!toast) return;
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
    toast.onClose?.(reason);
  },
}));
